import * as React from 'react';

import { translate } from '~l10n/client';
import { SmallButton } from '~shared/components/buttons';
import { DialogConfirmation } from '~shared/components/Dialog';

export type InviteConfirmAction = 'reset' | 'remove';

interface Props {
  action: InviteConfirmAction;
  roleLabel: string;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Confirmation for the destructive per-role invite actions. Reset rotates the
 * code (old link stops working); Remove turns the invite off until an Admin
 * creates a new one.
 */
export function InviteActionConfirmDialog({
  action,
  roleLabel,
  open,
  onClose,
  onConfirm,
}: Props) {
  const copy =
    action === 'reset'
      ? {
          title: translate('Reset invite for {0}?').format(roleLabel),
          body: translate(
            "Resetting replaces the current link — anyone with the old one won't be able to join."
          ),
          confirmLabel: translate('Reset'),
        }
      : {
          title: translate('Remove invite for {0}?').format(roleLabel),
          body: translate(
            'Removing this invite stops new people from joining as {0} until an Admin creates a new invite.'
          ).format(roleLabel),
          confirmLabel: translate('Remove'),
        };

  return (
    <DialogConfirmation
      id={`invite-${action}-confirm`}
      open={open}
      onClose={onClose}
      closeLabel={translate('Cancel')}
      title={copy.title}
      actions={[
        <SmallButton key="cancel" onClick={onClose}>
          {translate('Cancel')}
        </SmallButton>,
        <SmallButton key="confirm" appearance="primary" onClick={onConfirm}>
          {copy.confirmLabel}
        </SmallButton>,
      ]}
    >
      {copy.body}
    </DialogConfirmation>
  );
}
